import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Button } from '@/design-system/Button'
import { Card, Skeleton } from '@/design-system/primitives'
import type {
  FeedbackAnalysis,
  FeedbackResponsePage,
  FeedbackResponseRow,
  FeedbackSettings,
  FeedbackText,
  InvitationPlan,
  RecommendationKey,
} from '@/features/feedback/types'
import { RATING_KEYS, RECOMMENDATION_KEYS } from '@/features/feedback/types'
import { ApiError, apiClient } from '@/lib/apiClient'
import { cn } from '@/lib/cn'
import { formatDate, formatTime } from '@/lib/datetime'
import { accessToken } from '@/lib/staffSession'

import { useAdminMutation, useAdminQuery } from '../api'
import { AdminHeader, SaveBar } from '../AdminLayout'
import { I18nField, SelectField, SwitchField, TextField } from '../components/fields'
import { useAdminContext } from '../session'

type Tab = 'analysis' | 'responses' | 'settings' | 'invite'

const PAGE_SIZE = 20

/**
 * Les avis des invités : ce qu'ils ont pensé de la soirée, anonymement.
 *
 * Quatre onglets :
 * * **Analyse** — calculée par des règles, sans IA : moyenne, répartition,
 *   indice de recommandation, catégories classées, évolution, mots qui
 *   reviennent, et un résumé avec bons points et points à améliorer ;
 * * **Avis** — chaque réponse, en entier, à marquer lue ou à supprimer (spam) ;
 * * **Réglages & textes** — ouvrir / fermer le recueil, réécrire chaque texte
 *   du formulaire dans les trois langues ;
 * * **Inviter** — envoyer le lien d'avis aux invités, une fois par personne.
 *
 * Les filtres valent pour l'analyse, la liste et l'export : on lit, on compte
 * et on exporte la même sélection.
 */
export function AdminFeedbackPage() {
  const { t } = useTranslation()
  const { eventId, can } = useAdminContext()
  const label = useFeedbackLabels(eventId)
  const [tab, setTab] = useState<Tab>('analysis')

  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [scoreMin, setScoreMin] = useState('')
  const [scoreMax, setScoreMax] = useState('')
  const [recommendation, setRecommendation] = useState<'' | RecommendationKey>('')
  const [unreadOnly, setUnreadOnly] = useState(false)

  useEffect(() => {
    document.title = t('admin.section.feedback')
  }, [t])

  const filters = useMemo(() => {
    const params = new URLSearchParams()
    if (dateFrom) params.set('date_from', dateFrom)
    if (dateTo) params.set('date_to', dateTo)
    if (scoreMin) params.set('score_min', scoreMin)
    if (scoreMax) params.set('score_max', scoreMax)
    if (recommendation) params.set('recommendation', recommendation)
    return params
  }, [dateFrom, dateTo, scoreMin, scoreMax, recommendation])

  const tabs: Tab[] = [
    'analysis',
    'responses',
    'settings',
    ...(can('feedback.invitation.send') ? (['invite'] as Tab[]) : []),
  ]

  const unread = useAdminQuery<{ unread: number }>(
    ['admin-feedback-unread', eventId],
    `/admin/events/${eventId}/feedback/unread`,
    eventId,
  )

  const scoreOptions = [
    { value: '', label: t('admin.feedback.filters.any') },
    ...Array.from({ length: 11 }, (_, score) => ({ value: String(score), label: String(score) })),
  ]
  const filtersReset = () => {
    setDateFrom('')
    setDateTo('')
    setScoreMin('')
    setScoreMax('')
    setRecommendation('')
    setUnreadOnly(false)
  }
  const filtered = filters.toString() !== '' || unreadOnly

  return (
    <>
      <AdminHeader
        title={t('admin.section.feedback')}
        description={t('admin.feedback.description')}
        action={
          can('feedback.response.export') ? (
            <ExportButton eventId={eventId} filters={filters} unreadOnly={unreadOnly} />
          ) : null
        }
      />

      <div
        role="tablist"
        aria-label={t('admin.section.feedback')}
        className="mb-6 flex flex-wrap gap-1 rounded-2xl border border-line p-1 sm:inline-flex sm:rounded-full"
      >
        {tabs.map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={tab === value}
            onClick={() => setTab(value)}
            className={cn(
              'min-h-10 flex-1 rounded-full px-4 py-1.5 text-sm transition sm:flex-none',
              tab === value
                ? 'bg-bg-overlay text-content-primary'
                : 'text-content-muted hover:text-content-secondary',
            )}
          >
            {t(`admin.feedback.tab.${value}`)}
            {value === 'responses' && (unread.data?.unread ?? 0) > 0 ? (
              <span className="ml-2 rounded-full bg-ember px-2 py-0.5 text-xs text-white">
                {unread.data?.unread}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {tab === 'analysis' || tab === 'responses' ? (
        <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <TextField
            type="date"
            label={t('admin.feedback.filters.from')}
            value={dateFrom}
            onChange={setDateFrom}
          />
          <TextField
            type="date"
            label={t('admin.feedback.filters.to')}
            value={dateTo}
            onChange={setDateTo}
          />
          <SelectField
            label={t('admin.feedback.filters.scoreMin')}
            value={scoreMin}
            onChange={setScoreMin}
            options={scoreOptions}
          />
          <SelectField
            label={t('admin.feedback.filters.scoreMax')}
            value={scoreMax}
            onChange={setScoreMax}
            options={scoreOptions}
          />
          <SelectField<'' | RecommendationKey>
            label={t('admin.feedback.filters.recommendation')}
            value={recommendation}
            onChange={setRecommendation}
            options={[
              { value: '', label: t('admin.feedback.filters.any') },
              ...RECOMMENDATION_KEYS.map((key) => ({
                value: key,
                label: label(`recommend.${key}`, `admin.feedback.recommendation.${key}`),
              })),
            ]}
          />
          <div className="flex items-end">
            {filtered ? (
              <Button variant="ghost" size="sm" onClick={filtersReset}>
                {t('admin.feedback.filters.reset')}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      {tab === 'analysis' ? <AnalysisTab eventId={eventId} filters={filters} /> : null}
      {tab === 'responses' ? (
        <ResponsesTab
          eventId={eventId}
          filters={filters}
          unreadOnly={unreadOnly}
          setUnreadOnly={setUnreadOnly}
        />
      ) : null}
      {tab === 'settings' ? <SettingsTab eventId={eventId} /> : null}
      {tab === 'invite' ? <InviteTab eventId={eventId} /> : null}
    </>
  )
}

/**
 * Les libellés de l'édition (catégories, choix) dans la langue de l'écran —
 * ceux que l'équipe a réécrits, pas ceux du code : l'avis se lit avec les
 * mots que l'invité a vus.
 */
function useFeedbackLabels(eventId: string | undefined) {
  const { t, i18n } = useTranslation()
  const settings = useAdminQuery<FeedbackSettings>(
    ['admin-feedback-settings', eventId],
    `/admin/events/${eventId}/feedback/settings`,
    eventId,
  )
  return (key: string, fallback: string): string => {
    const text = settings.data?.texts.find((item) => item.key === key)
    const lang = i18n.language
    return text?.value[lang] || text?.default[lang] || t(fallback)
  }
}

/* ------------------------------------------------------------------ */
/* Analyse                                                             */
/* ------------------------------------------------------------------ */

function AnalysisTab({ eventId, filters }: { eventId: string | undefined; filters: URLSearchParams }) {
  const { t, i18n } = useTranslation()
  const { event } = useAdminContext()
  const params = new URLSearchParams(filters)
  params.set('locale', i18n.language)
  const query = useAdminQuery<FeedbackAnalysis>(
    ['admin-feedback-analysis', eventId, params.toString()],
    `/admin/events/${eventId}/feedback/analysis?${params.toString()}`,
    eventId,
    { refetchInterval: 60_000 },
  )
  const number = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 })

  if (query.isPending) return <Skeleton className="h-72 w-full" />
  if (query.isError) return <p className="text-sm text-state-danger">{query.error.message}</p>
  const data = query.data
  const score = data.score

  return (
    <div className="space-y-6">
      <Card>
        <h2 className="font-display text-heading-md text-content-primary">
          {t('admin.feedback.analysis.summary')}
        </h2>
        <p className="mt-3 max-w-3xl text-content-secondary">{data.summary}</p>
        {data.count > 0 ? (
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            <PointList
              title={t('admin.feedback.analysis.strengths')}
              items={data.strengths}
              empty={t('admin.feedback.analysis.noStrengths')}
              tone="good"
            />
            <PointList
              title={t('admin.feedback.analysis.improvements')}
              items={data.improvements}
              empty={t('admin.feedback.analysis.noImprovements')}
              tone="bad"
            />
          </div>
        ) : null}
      </Card>

      {data.count > 0 ? (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Tile label={t('admin.feedback.analysis.count')} value={number.format(data.count)} />
            <Tile
              label={t('admin.feedback.analysis.average')}
              value={score.average !== null ? `${number.format(score.average)}/10` : '—'}
              sub={
                score.median !== null
                  ? t('admin.feedback.analysis.median', { value: number.format(score.median) })
                  : undefined
              }
            />
            <Tile
              label={t('admin.feedback.analysis.index')}
              value={
                data.recommendation.index === null
                  ? '—'
                  : data.recommendation.index > 0
                    ? `+${data.recommendation.index}`
                    : String(data.recommendation.index)
              }
              sub={t('admin.feedback.analysis.indexRange')}
            />
            <Tile
              label={t('admin.feedback.analysis.groups')}
              value={`${share(score.enthusiasts, data.count)} %`}
              sub={t('admin.feedback.analysis.groupsSub', {
                satisfied: share(score.satisfied, data.count),
                disappointed: share(score.disappointed, data.count),
              })}
            />
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <h2 className="text-sm font-medium text-content-primary">
                {t('admin.feedback.analysis.distribution')}
              </h2>
              <ScoreBars distribution={score.distribution} />
            </Card>

            <Card>
              <h2 className="text-sm font-medium text-content-primary">
                {t('admin.feedback.analysis.recommendation')}
              </h2>
              <ul className="mt-4 space-y-3">
                {data.recommendation.choices.map((choice) => (
                  <li key={choice.key}>
                    <div className="flex justify-between gap-3 text-sm">
                      <span className="min-w-0 break-words text-content-secondary">{choice.label}</span>
                      <span className="shrink-0 tabular-nums text-content-primary">
                        {choice.share} % · {choice.count}
                      </span>
                    </div>
                    <Bar ratio={choice.share / 100} label={`${choice.label} : ${choice.share} %`} />
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-content-muted">{t('admin.feedback.analysis.indexHelp')}</p>
            </Card>
          </div>

          <Card>
            <h2 className="text-sm font-medium text-content-primary">
              {t('admin.feedback.analysis.categories')}
            </h2>
            <ol className="mt-4 space-y-3">
              {data.categories.map((category) => (
                <li key={category.key}>
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-sm">
                    <span className="min-w-0 break-words text-content-secondary">
                      <span className="mr-2 tabular-nums text-content-muted">{category.rank}.</span>
                      {category.label}
                      {category.verdict !== 'neutral' ? (
                        <span
                          className={cn(
                            'ml-2 inline-block rounded-full border px-2 py-0.5 text-xs',
                            category.verdict === 'strength'
                              ? 'border-state-success/40 text-state-success'
                              : 'border-ember-400/40 text-ember-300',
                          )}
                        >
                          {t(`admin.feedback.verdict.${category.verdict}`)}
                        </span>
                      ) : null}
                    </span>
                    <span className="shrink-0 tabular-nums text-content-primary">
                      {category.average !== null ? `${number.format(category.average)}/5` : '—'}
                    </span>
                  </div>
                  <Bar
                    ratio={(category.average ?? 0) / 5}
                    label={`${category.label} : ${category.average ?? '—'}/5`}
                  />
                </li>
              ))}
            </ol>
            <p className="mt-4 text-xs text-content-muted">{t('admin.feedback.analysis.categoriesHelp')}</p>
          </Card>

          {data.days.length > 0 ? (
            <Card>
              <h2 className="text-sm font-medium text-content-primary">
                {t('admin.feedback.analysis.trend')}
              </h2>
              <ul className="mt-4 space-y-2">
                {data.days.map((day) => (
                  <li
                    key={day.day}
                    className="grid grid-cols-[minmax(0,7rem)_1fr_auto] items-center gap-3 text-sm"
                  >
                    <span className="truncate text-content-secondary">
                      {new Intl.DateTimeFormat(i18n.language, {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                        timeZone: 'UTC',
                      }).format(new Date(`${day.day}T00:00:00Z`))}
                    </span>
                    <Bar
                      ratio={day.count / Math.max(...data.days.map((entry) => entry.count))}
                      label={t('admin.feedback.analysis.dayDetail', {
                        count: day.count,
                        average: number.format(day.average),
                      })}
                      inline
                    />
                    <span className="shrink-0 tabular-nums text-content-primary">
                      {t('admin.feedback.analysis.dayDetail', {
                        count: day.count,
                        average: number.format(day.average),
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

          <div className="grid gap-6 lg:grid-cols-2">
            {data.texts.map((theme) => (
              <Card key={theme.field}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h2 className="text-sm font-medium text-content-primary">
                    {t(`admin.feedback.field.${theme.field}`)}
                  </h2>
                  <span className="text-xs text-content-muted">
                    {t('admin.feedback.analysis.answered', { count: theme.answered })}
                  </span>
                </div>
                {theme.answered === 0 ? (
                  <p className="mt-3 text-sm text-content-muted">{t('admin.feedback.analysis.noText')}</p>
                ) : (
                  <>
                    {theme.words.length > 0 ? (
                      <Chips title={t('admin.feedback.analysis.words')} terms={theme.words} />
                    ) : null}
                    {theme.expressions.length > 0 ? (
                      <Chips
                        title={t('admin.feedback.analysis.expressions')}
                        terms={theme.expressions}
                      />
                    ) : null}
                    <ul className="mt-4 space-y-2">
                      {theme.quotes.map((quote) => (
                        <li
                          key={quote}
                          className="break-words border-l-2 border-champagne-400/50 pl-3 text-sm italic text-content-secondary"
                        >
                          « {quote} »
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </Card>
            ))}
          </div>
        </>
      ) : null}

      <p className="max-w-3xl text-xs text-content-muted">
        {t('admin.feedback.analysis.method', { timezone: event?.timezone ?? 'UTC' })}
      </p>
    </div>
  )
}

function share(part: number, total: number): number {
  return total ? Math.round((part / total) * 100) : 0
}

function Tile({ label, value, sub }: { label: string; value: string; sub?: string | undefined }) {
  return (
    <Card className="min-w-0 p-4 sm:p-6">
      <p className="text-caption uppercase tracking-wide text-content-muted">{label}</p>
      <p className="mt-2 font-display text-display-sm tabular-nums text-content-primary">{value}</p>
      {sub ? <p className="mt-1 text-xs text-content-muted">{sub}</p> : null}
    </Card>
  )
}

function PointList({
  title,
  items,
  empty,
  tone,
}: {
  title: string
  items: string[]
  empty: string
  tone: 'good' | 'bad'
}) {
  return (
    <div className="min-w-0">
      <h3
        className={cn(
          'text-caption uppercase tracking-wide',
          tone === 'good' ? 'text-state-success' : 'text-ember-300',
        )}
      >
        {title}
      </h3>
      {items.length === 0 ? (
        <p className="mt-2 text-sm text-content-muted">{empty}</p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {items.map((item) => (
            <li key={item} className="flex gap-2 text-sm text-content-primary">
              <span aria-hidden className={tone === 'good' ? 'text-state-success' : 'text-ember-300'}>
                {tone === 'good' ? '+' : '–'}
              </span>
              <span className="min-w-0 break-words">{item}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** Une barre horizontale, une seule teinte : une grandeur, pas une identité. */
function Bar({ ratio, label, inline = false }: { ratio: number; label: string; inline?: boolean }) {
  const width = Math.max(0, Math.min(1, ratio)) * 100
  return (
    <div
      role="img"
      aria-label={label}
      title={label}
      className={cn('h-1.5 rounded-full bg-bg-overlay', inline ? 'min-w-0' : 'mt-1')}
    >
      <div
        className="h-full rounded-full bg-champagne-400/70 transition-[width] duration-500"
        style={{ width: `${width}%` }}
      />
    </div>
  )
}

/** Onze colonnes, de 0 à 10 ; le survol ou le focus donne le nombre exact. */
function ScoreBars({ distribution }: { distribution: number[] }) {
  const { t } = useTranslation()
  const [active, setActive] = useState<number | null>(null)
  const max = Math.max(1, ...distribution)
  return (
    <div className="mt-4">
      <p className="h-5 text-sm text-content-secondary" aria-live="polite">
        {active !== null
          ? t('admin.feedback.analysis.scoreDetail', {
              score: active,
              count: distribution[active] ?? 0,
            })
          : t('admin.feedback.analysis.scoreHint')}
      </p>
      <div
        className="mt-3 flex h-40 items-end gap-[2px] border-b border-line-subtle"
        onMouseLeave={() => setActive(null)}
      >
        {distribution.map((count, score) => (
          <button
            key={score}
            type="button"
            onMouseEnter={() => setActive(score)}
            onFocus={() => setActive(score)}
            onBlur={() => setActive(null)}
            aria-label={t('admin.feedback.analysis.scoreDetail', { score, count })}
            className="group flex h-full min-w-0 flex-1 items-end"
          >
            <span
              className={cn(
                'block w-full rounded-t-[4px] transition-[height,background-color] duration-500',
                score === active ? 'bg-champagne-300' : 'bg-champagne-400/60 group-hover:bg-champagne-300',
              )}
              style={{ height: `${count ? Math.max(3, (count / max) * 100) : 0}%` }}
            />
          </button>
        ))}
      </div>
      <div className="mt-1 flex gap-[2px] text-center text-caption tabular-nums text-content-muted">
        {distribution.map((_, score) => (
          <span key={score} className="min-w-0 flex-1">
            {score}
          </span>
        ))}
      </div>
    </div>
  )
}

function Chips({ title, terms }: { title: string; terms: { term: string; count: number }[] }) {
  return (
    <div className="mt-3">
      <p className="text-xs text-content-muted">{title}</p>
      <ul className="mt-1.5 flex flex-wrap gap-1.5">
        {terms.map((term) => (
          <li
            key={term.term}
            className="max-w-full break-words rounded-full border border-line px-2.5 py-0.5 text-sm text-content-primary"
          >
            {term.term} <span className="tabular-nums text-content-muted">{term.count}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Avis                                                                */
/* ------------------------------------------------------------------ */

function ResponsesTab({
  eventId,
  filters,
  unreadOnly,
  setUnreadOnly,
}: {
  eventId: string | undefined
  filters: URLSearchParams
  unreadOnly: boolean
  setUnreadOnly: (value: boolean) => void
}) {
  const { t } = useTranslation()
  const { can } = useAdminContext()
  const [page, setPage] = useState(1)
  const [deleting, setDeleting] = useState<FeedbackResponseRow | null>(null)
  const params = new URLSearchParams(filters)
  if (unreadOnly) params.set('unread', 'true')
  const key = params.toString()

  useEffect(() => setPage(1), [key])

  const query = useAdminQuery<FeedbackResponsePage>(
    ['admin-feedback-responses', eventId, key, page],
    `/admin/events/${eventId}/feedback/responses?${key}&page=${page}&page_size=${PAGE_SIZE}`,
    eventId,
  )
  const invalidate: string[][] = [
    ['admin-feedback-responses'],
    ['admin-feedback-unread'],
    ['admin-feedback-analysis'],
  ]
  const mark = useAdminMutation<FeedbackResponseRow, { id: string; is_read: boolean }>(
    'patch',
    ({ id }) => `/admin/events/${eventId}/feedback/responses/${id}`,
    eventId,
    invalidate,
  )
  const markAll = useAdminMutation<unknown, Record<string, never>>(
    'post',
    () => `/admin/events/${eventId}/feedback/responses/read-all`,
    eventId,
    invalidate,
  )

  const data = query.data
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex min-h-10 items-center gap-2 text-sm text-content-secondary">
          <input
            type="checkbox"
            checked={unreadOnly}
            onChange={(change) => setUnreadOnly(change.target.checked)}
            className="h-4 w-4 accent-[var(--ember-500)]"
          />
          {t('admin.feedback.list.unreadOnly')}
        </label>
        {data && data.unread > 0 ? (
          <Button
            variant="outline"
            size="sm"
            disabled={markAll.isPending}
            onClick={() => markAll.mutate({})}
          >
            {t('admin.feedback.list.markAll', { count: data.unread })}
          </Button>
        ) : null}
      </div>

      {query.isPending ? <Skeleton className="h-64 w-full" /> : null}
      {query.isError ? <p className="text-sm text-state-danger">{query.error.message}</p> : null}
      {data && data.items.length === 0 ? (
        <p className="rounded-md border border-line-subtle p-6 text-center text-sm text-content-muted">
          {t('admin.feedback.list.empty')}
        </p>
      ) : null}

      {data ? (
        <>
          <p className="text-xs text-content-muted">
            {t('admin.feedback.list.total', { count: data.total })}
          </p>
          <ul className="space-y-3">
            {data.items.map((row) => (
              <ResponseCard
                key={row.id}
                row={row}
                onToggleRead={() => mark.mutate({ id: row.id, is_read: !row.is_read })}
                onDelete={can('feedback.response.delete') ? () => setDeleting(row) : null}
              />
            ))}
          </ul>
          {data.pages > 1 ? (
            <div className="flex items-center justify-between gap-3">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                {t('admin.feedback.list.previous')}
              </Button>
              <span className="text-xs tabular-nums text-content-muted">
                {page} / {data.pages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={page >= data.pages}
                onClick={() => setPage(page + 1)}
              >
                {t('admin.feedback.list.next')}
              </Button>
            </div>
          ) : null}
        </>
      ) : null}

      {deleting ? (
        <DeleteDialog eventId={eventId} row={deleting} onClose={() => setDeleting(null)} />
      ) : null}
    </div>
  )
}

function ResponseCard({
  row,
  onToggleRead,
  onDelete,
}: {
  row: FeedbackResponseRow
  onToggleRead: () => void
  onDelete: (() => void) | null
}) {
  const { t, i18n } = useTranslation()
  const { event, eventId } = useAdminContext()
  const label = useFeedbackLabels(eventId)
  const zone = event?.timezone ?? 'UTC'
  return (
    <li
      className={cn(
        'min-w-0 rounded-lg border bg-bg-elevated p-4 sm:p-5',
        row.is_read ? 'border-line-subtle' : 'border-champagne-400/50',
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span
            className={cn(
              'flex h-12 w-12 shrink-0 items-center justify-center rounded-full font-display text-heading-md tabular-nums',
              row.score >= 9
                ? 'bg-ember text-white'
                : row.score >= 7
                  ? 'border border-champagne-400 text-champagne-300'
                  : 'border border-line text-content-primary',
            )}
            aria-label={t('admin.feedback.list.score', { score: row.score })}
          >
            {row.score}
          </span>
          <div className="min-w-0">
            <p className="break-words text-content-primary">
              {label(
                `recommend.${row.recommendation}`,
                `admin.feedback.recommendation.${row.recommendation}`,
              )}
            </p>
            <p className="text-caption text-content-muted">
              {formatDate(row.created_at, i18n.language, zone)} ·{' '}
              {formatTime(row.created_at, i18n.language, zone)} · {row.locale.toUpperCase()}
              {!row.is_read ? (
                <span className="ml-2 rounded-full bg-ember px-2 py-0.5 text-[0.7rem] text-white">
                  {t('admin.feedback.list.new')}
                </span>
              ) : null}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={onToggleRead}>
            {row.is_read ? t('admin.feedback.list.markUnread') : t('admin.feedback.list.markRead')}
          </Button>
          {onDelete ? (
            <Button variant="ghost" size="sm" onClick={onDelete}>
              {t('admin.feedback.list.delete')}
            </Button>
          ) : null}
        </div>
      </div>

      <dl className="mt-4 grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
        {RATING_KEYS.map((key) => (
          <div key={key} className="flex items-center justify-between gap-3 text-sm">
            <dt className="min-w-0 break-words text-content-secondary">
              {label(`category.${key}`, `admin.feedback.category.${key}`)}
            </dt>
            <dd
              className="shrink-0 tracking-widest text-champagne-400"
              aria-label={t('admin.feedback.list.stars', { count: row.ratings[key] })}
            >
              {'★'.repeat(row.ratings[key])}
              <span className="text-content-muted/50">{'★'.repeat(5 - row.ratings[key])}</span>
            </dd>
          </div>
        ))}
      </dl>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {(['liked', 'disliked', 'improve', 'suggestions'] as const).map((field) =>
          row[field] ? (
            <div key={field} className="min-w-0">
              <p className="text-caption uppercase tracking-wide text-content-muted">
                {t(`admin.feedback.field.${field}`)}
              </p>
              <p className="mt-1 whitespace-pre-line break-words text-sm text-content-primary">
                {row[field]}
              </p>
            </div>
          ) : null,
        )}
      </div>
    </li>
  )
}

function Dialog({
  title,
  children,
  onClose,
  pending,
}: {
  title: string
  children: ReactNode
  onClose: () => void
  pending: boolean
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !pending) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, pending])
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-4 sm:items-center"
      role="dialog"
      aria-modal
      aria-labelledby="feedback-dialog-title"
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-line bg-bg-base p-6">
        <h2 id="feedback-dialog-title" className="font-display text-heading-md text-content-primary">
          {title}
        </h2>
        {children}
      </div>
    </div>
  )
}

function DeleteDialog({
  eventId,
  row,
  onClose,
}: {
  eventId: string | undefined
  row: FeedbackResponseRow
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [reason, setReason] = useState('')
  const queryClient = useQueryClient()
  // Le motif voyage dans le corps de la requête : `useAdminMutation` n'en
  // envoie pas sur un DELETE.
  const remove = useMutation({
    mutationFn: async (body: { reason: string }) =>
      apiClient.delete(`/admin/events/${eventId}/feedback/responses/${row.id}`, {
        token: await accessToken(),
        ...(eventId ? { eventId } : {}),
        body,
      }),
    onSuccess: () => {
      for (const key of ['admin-feedback-responses', 'admin-feedback-unread', 'admin-feedback-analysis']) {
        void queryClient.invalidateQueries({ queryKey: [key] })
      }
    },
  })
  return (
    <Dialog title={t('admin.feedback.delete.title')} onClose={onClose} pending={remove.isPending}>
      <p className="mt-3 text-sm text-content-secondary">{t('admin.feedback.delete.body')}</p>
      <div className="mt-4">
        <TextField
          label={t('admin.feedback.delete.reason')}
          placeholder={t('admin.feedback.delete.reasonPlaceholder')}
          value={reason}
          maxLength={300}
          onChange={setReason}
        />
      </div>
      {remove.isError ? (
        <p className="mt-3 text-sm text-state-danger">{remove.error.message}</p>
      ) : null}
      <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <Button variant="ghost" onClick={onClose} disabled={remove.isPending}>
          {t('admin.feedback.cancel')}
        </Button>
        <Button
          disabled={remove.isPending || reason.trim().length < 3}
          onClick={() => remove.mutate({ reason: reason.trim() }, { onSuccess: onClose })}
        >
          {t('admin.feedback.delete.confirm')}
        </Button>
      </div>
    </Dialog>
  )
}

function ExportButton({
  eventId,
  filters,
  unreadOnly,
}: {
  eventId: string | undefined
  filters: URLSearchParams
  unreadOnly: boolean
}) {
  const { t } = useTranslation()
  const exporter = useMutation({
    mutationFn: async () => {
      const prefix = import.meta.env.VITE_API_PREFIX ?? '/api/v1'
      const params = new URLSearchParams(filters)
      if (unreadOnly) params.set('unread', 'true')
      const response = await fetch(
        `${prefix}/admin/events/${eventId}/feedback/responses/export?${params.toString()}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await accessToken()}`,
            ...(eventId ? { 'X-Event-Id': eventId } : {}),
          },
          body: JSON.stringify({ reason: null }),
        },
      )
      if (!response.ok) throw new Error(String(response.status))
      const disposition = response.headers.get('Content-Disposition') ?? ''
      const match = /filename="([^"]+)"/.exec(disposition)
      const url = URL.createObjectURL(await response.blob())
      const link = document.createElement('a')
      link.href = url
      link.download = match?.[1] ?? 'avis.csv'
      link.click()
      window.setTimeout(() => URL.revokeObjectURL(url), 1000)
    },
  })
  return (
    <div className="flex flex-col items-start gap-1 sm:items-end">
      <Button variant="outline" size="sm" disabled={exporter.isPending} onClick={() => exporter.mutate()}>
        {exporter.isPending ? t('admin.feedback.exporting') : t('admin.feedback.export')}
      </Button>
      {exporter.isError ? (
        <span className="text-sm text-state-danger">{t('admin.feedback.exportFailed')}</span>
      ) : null}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Réglages et textes                                                  */
/* ------------------------------------------------------------------ */

const GROUPS: FeedbackText['group'][] = ['page', 'score', 'categories', 'texts', 'recommend', 'end']
const LOCALES = ['fr', 'de', 'en'] as const

function effective(text: FeedbackText): Record<string, string> {
  return Object.fromEntries(
    LOCALES.map((locale) => [locale, text.value[locale] || text.default[locale] || '']),
  )
}

function SettingsTab({ eventId }: { eventId: string | undefined }) {
  const { t } = useTranslation()
  const { can } = useAdminContext()
  const queryClient = useQueryClient()
  const editable = can('feedback.settings.update')
  const query = useAdminQuery<FeedbackSettings>(
    ['admin-feedback-settings', eventId],
    `/admin/events/${eventId}/feedback/settings`,
    eventId,
  )
  const save = useAdminMutation<FeedbackSettings, { is_open?: boolean; texts?: Record<string, Record<string, string>> }>(
    'put',
    () => `/admin/events/${eventId}/feedback/settings`,
    eventId,
    [['admin-feedback-settings'], ['admin-qr-codes']],
  )
  const [drafts, setDrafts] = useState<Record<string, Record<string, string>> | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)
  const [copied, setCopied] = useState(false)

  const data = query.data
  const initial = useMemo(
    () => (data ? Object.fromEntries(data.texts.map((text) => [text.key, effective(text)])) : {}),
    [data],
  )
  const current = drafts ?? initial
  const dirty = drafts !== null && JSON.stringify(drafts) !== JSON.stringify(initial)

  if (query.isPending) return <Skeleton className="h-72 w-full" />
  if (query.isError || !data) return <p className="text-sm text-state-danger">{query.error?.message}</p>

  const saveTexts = () =>
    save.mutate(
      { texts: current },
      {
        onSuccess: (fresh) => {
          queryClient.setQueryData(['admin-feedback-settings', eventId], fresh)
          setDrafts(null)
          setSavedAt(Date.now())
        },
      },
    )

  return (
    <div className="space-y-6">
      <Card className="space-y-4">
        <SwitchField
          label={t('admin.feedback.settings.open')}
          hint={t('admin.feedback.settings.openHint')}
          value={data.is_open}
          disabled={!editable || save.isPending}
          onChange={(value) => save.mutate({ is_open: value })}
        />
        <div className="min-w-0">
          <p className="text-caption uppercase tracking-wide text-content-muted">
            {t('admin.feedback.settings.link')}
          </p>
          <p className="mt-1 break-all font-mono text-sm text-content-primary">{data.public_url}</p>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-caption">
            <button
              type="button"
              className="text-champagne-300 underline"
              onClick={() =>
                void navigator.clipboard
                  ?.writeText(data.public_url)
                  .then(() => {
                    setCopied(true)
                    window.setTimeout(() => setCopied(false), 2000)
                  })
                  .catch(() => setCopied(false))
              }
            >
              {copied ? t('admin.feedback.settings.copied') : t('admin.feedback.settings.copy')}
            </button>
            <Link to="/admin/qr-codes" className="text-champagne-300 underline">
              {t('admin.feedback.settings.qr')}
            </Link>
          </div>
        </div>
        <p className="text-sm text-content-secondary">
          {t('admin.feedback.settings.recipients', { count: data.recipients })}{' '}
          <Link to="/admin/notifications" className="text-champagne-300 underline">
            {t('admin.feedback.settings.recipientsLink')}
          </Link>
        </p>
        <p className="text-xs text-content-muted">{t('admin.feedback.settings.anonymity')}</p>
      </Card>

      {GROUPS.map((group) => (
        <section key={group} className="rounded-lg border border-line-subtle bg-bg-elevated p-4 sm:p-6">
          <h2 className="font-display text-heading-md text-content-primary">
            {t(`admin.feedback.textGroup.${group}`)}
          </h2>
          <div className="mt-5 grid gap-5">
            {data.texts
              .filter((text) => text.group === group)
              .map((text) => {
                const value = current[text.key] ?? effective(text)
                const changed = LOCALES.some(
                  (locale) => (value[locale] ?? '') !== (text.default[locale] ?? ''),
                )
                return (
                  <div key={text.key} className="min-w-0">
                    <I18nField
                      label={t(`admin.feedback.text.${text.key.replace('.', '_')}`)}
                      value={value}
                      multiline={text.multiline}
                      locales={LOCALES}
                      onChange={(next) =>
                        editable && setDrafts({ ...current, [text.key]: next })
                      }
                    />
                    {editable && changed ? (
                      <button
                        type="button"
                        className="mt-1 text-caption text-champagne-300 underline"
                        onClick={() => setDrafts({ ...current, [text.key]: { ...text.default } })}
                      >
                        {t('admin.feedback.settings.restore')}
                      </button>
                    ) : null}
                  </div>
                )
              })}
          </div>
        </section>
      ))}

      {editable ? (
        <SaveBar
          dirty={dirty}
          saving={save.isPending}
          error={save.isError ? save.error.message : null}
          savedAt={savedAt}
          onSave={saveTexts}
          onReset={() => setDrafts(null)}
        />
      ) : (
        <p className="text-xs text-content-muted">{t('admin.feedback.settings.readOnly')}</p>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Inviter                                                             */
/* ------------------------------------------------------------------ */

function InviteTab({ eventId }: { eventId: string | undefined }) {
  const { t } = useTranslation()
  const [consentOnly, setConsentOnly] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [result, setResult] = useState<{ queued: number; already_invited: number } | null>(null)
  const plan = useAdminQuery<InvitationPlan>(
    ['admin-feedback-invitations', eventId, consentOnly],
    `/admin/events/${eventId}/feedback/invitations?consent_only=${consentOnly}`,
    eventId,
  )
  const send = useAdminMutation<{ queued: number; already_invited: number }, { consent_only: boolean }>(
    'post',
    () => `/admin/events/${eventId}/feedback/invitations`,
    eventId,
    [['admin-feedback-invitations']],
  )
  const data = plan.data

  return (
    <div className="space-y-6">
      <Card className="space-y-4">
        <p className="max-w-3xl text-sm text-content-secondary">{t('admin.feedback.invite.intro')}</p>
        <label className="flex min-h-10 items-start gap-2 text-sm text-content-secondary">
          <input
            type="checkbox"
            checked={consentOnly}
            onChange={(change) => setConsentOnly(change.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--ember-500)]"
          />
          <span className="min-w-0">{t('admin.feedback.invite.consentOnly')}</span>
        </label>
        <p className="text-xs text-content-muted">{t('admin.feedback.invite.legal')}</p>

        {plan.isPending ? <Skeleton className="h-16 w-full" /> : null}
        {data ? (
          <dl className="grid grid-cols-3 gap-3">
            <InviteStat label={t('admin.feedback.invite.eligible')} value={data.eligible} />
            <InviteStat label={t('admin.feedback.invite.already')} value={data.already_invited} />
            <InviteStat label={t('admin.feedback.invite.toSend')} value={data.to_send} />
          </dl>
        ) : null}

        <Button
          disabled={!data || data.to_send === 0}
          className="h-auto min-h-12 w-full min-w-0 whitespace-normal py-2 sm:w-auto"
          onClick={() => {
            setResult(null)
            setConfirming(true)
          }}
        >
          {t('admin.feedback.invite.send')}
        </Button>
        {data && data.to_send === 0 ? (
          <p className="text-sm text-content-muted">{t('admin.feedback.invite.nobody')}</p>
        ) : null}
        {result ? (
          <p role="status" className="text-sm text-state-success">
            {t('admin.feedback.invite.done', { count: result.queued })}
          </p>
        ) : null}
        <p className="text-xs text-content-muted">
          {t('admin.feedback.invite.template')}{' '}
          <Link to="/admin/messages" className="text-champagne-300 underline">
            {t('admin.feedback.invite.templateLink')}
          </Link>
        </p>
      </Card>

      {confirming && data ? (
        <Dialog
          title={t('admin.feedback.invite.confirmTitle')}
          onClose={() => setConfirming(false)}
          pending={send.isPending}
        >
          <p className="mt-3 text-sm text-content-secondary">
            {t('admin.feedback.invite.confirmBody', { count: data.to_send })}
          </p>
          <p className="mt-2 break-all font-mono text-xs text-content-muted">{data.public_url}</p>
          {send.isError ? (
            <p className="mt-3 text-sm text-state-danger">
              {send.error instanceof ApiError ? send.error.message : t('admin.feedback.invite.failed')}
            </p>
          ) : null}
          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button variant="ghost" onClick={() => setConfirming(false)} disabled={send.isPending}>
              {t('admin.feedback.cancel')}
            </Button>
            <Button
              disabled={send.isPending}
              onClick={() =>
                send.mutate(
                  { consent_only: consentOnly },
                  {
                    onSuccess: (sent) => {
                      setResult(sent)
                      setConfirming(false)
                    },
                  },
                )
              }
            >
              {t('admin.feedback.invite.confirm', { count: data.to_send })}
            </Button>
          </div>
        </Dialog>
      ) : null}
    </div>
  )
}

function InviteStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="min-w-0 rounded-md border border-line-subtle p-3">
      <dt className="break-words text-xs text-content-muted">{label}</dt>
      <dd className="mt-1 font-display text-heading-lg tabular-nums text-content-primary">{value}</dd>
    </div>
  )
}
